import { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, Bars3Icon } from '@heroicons/react/24/outline';
import customModelService from '../../services/customModelService';

export default function CreateEditModelModal({ model, onSave, onClose }) {
  const [modelName, setModelName] = useState(model?.model_name || '');
  const [description, setDescription] = useState(model?.description || '');
  const [selectedSport, setSelectedSport] = useState(model?.sport || 'nfl');
  const [aiProvider, setAiProvider] = useState(model?.ai_provider || 'knewton');
  const [attributes, setAttributes] = useState([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sports = [
    { value: 'nfl', label: 'NFL' },
    { value: 'nba', label: 'NBA' },
    { value: 'mlb', label: 'MLB' },
    { value: 'nhl', label: 'NHL' },
    { value: 'ncaa_f', label: 'NCAA Football' }
  ];

  const aiProviders = [
    { id: 'knewton', name: 'Knewton', description: 'Advanced AI-powered predictions' },
    { id: 'merlin', name: 'Merlin', description: 'Advanced AI-powered predictions' }
  ];

  const loadAttributes = useCallback(async () => {
    try {
      setLoading(true);
      const apiAttributes = await customModelService.getAttributes(selectedSport);

      if (model && model.sport === selectedSport && model.weights) {
        // Editing existing model - use saved weights
        const weightMap = {};
        model.weights.forEach(w => {
          weightMap[w.attribute_key] = w.weight;
        });

        setAttributes(apiAttributes.map(attr => ({
          ...attr,
          weight: weightMap[attr.attribute_key] || 0
        })));
      } else {
        // New model or sport changed - distribute weights equally
        const equalWeight = Math.floor(100 / apiAttributes.length);
        const remainder = 100 - (equalWeight * apiAttributes.length);

        setAttributes(apiAttributes.map((attr, index) => ({
          ...attr,
          weight: index === 0 ? equalWeight + remainder : equalWeight
        })));
      }
    } catch (error) {
      console.error('Error loading attributes:', error);
      alert('Failed to load attributes. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedSport, model]);

  // Load attributes from API when sport changes
  useEffect(() => {
    loadAttributes();
  }, [selectedSport, loadAttributes]);

  // Calculate total weight
  const totalWeight = attributes.reduce((sum, attr) => sum + attr.weight, 0);
  const weightError = Math.abs(totalWeight - 100) > 0.1;

  // Handle drag start
  const handleDragStart = (index) => {
    setDraggedIndex(index);
  };

  // Handle drag over
  const handleDragOver = (e, index) => {
    e.preventDefault();

    if (draggedIndex === null || draggedIndex === index) return;

    const newAttributes = [...attributes];
    const draggedItem = newAttributes[draggedIndex];

    // Remove from old position
    newAttributes.splice(draggedIndex, 1);

    // Insert at new position
    newAttributes.splice(index, 0, draggedItem);

    setAttributes(newAttributes);
    setDraggedIndex(index);
  };

  // Handle drag end
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // Handle weight change
  const handleWeightChange = (index, newWeight) => {
    const weight = Math.max(0, Math.min(100, parseInt(newWeight) || 0));
    const newAttributes = [...attributes];
    newAttributes[index].weight = weight;
    setAttributes(newAttributes);
  };

  // Auto-balance weights
  const handleAutoBalance = () => {
    const weight = Math.floor(100 / attributes.length);
    const newAttributes = attributes.map((attr, index) => ({
      ...attr,
      weight: index === 0 ? 100 - (weight * (attributes.length - 1)) : weight
    }));
    setAttributes(newAttributes);
  };

  // Handle save
  const handleSave = async () => {
    if (!modelName.trim()) {
      alert('Please enter a model name');
      return;
    }

    if (weightError) {
      alert('Weights must add up to 100%');
      return;
    }

    setSaving(true);
    try {
      const modelData = {
        model_name: modelName,
        sport: selectedSport,
        description: description || null,
        ai_provider: aiProvider,
        weights: attributes
          .filter(attr => attr.weight > 0)
          .map(attr => ({
            attribute_key: attr.attribute_key,
            weight: attr.weight
          }))
      };

      if (model) {
        await customModelService.updateModel(model.id, modelData);
      } else {
        await customModelService.createModel(modelData);
      }

      onSave(); // Refresh parent list
      onClose();
    } catch (error) {
      console.error('Error saving model:', error);
      alert(error.response?.data?.detail || 'Failed to save model. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="glass-card max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-bg-card/95 backdrop-blur-sm border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gradient">
            {model ? 'Edit Model' : 'Create New Model'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-secondary rounded-lg transition-colors"
          >
            <XMarkIcon className="w-6 h-6 text-text-secondary" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Model Name */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Model Name
            </label>
            <input
              type="text"
              value={modelName}
              onChange={(e) => setModelName(e.target.value)}
              placeholder="e.g., Conservative NFL Strategy"
              className="w-full input-glow"
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Description (Optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe your model strategy..."
              rows={3}
              className="w-full input-glow resize-none"
            />
          </div>

          {/* Sport Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Sport
            </label>
            <select
              value={selectedSport}
              onChange={(e) => setSelectedSport(e.target.value)}
              className="w-full input-glow"
              disabled={!!model} // Can't change sport when editing
            >
              {sports.map((sport) => (
                <option key={sport.value} value={sport.value}>
                  {sport.label}
                </option>
              ))}
            </select>
            {model && (
              <p className="mt-1 text-xs text-text-secondary">
                Sport cannot be changed when editing a model
              </p>
            )}
          </div>

          {/* AI Provider Selection */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              AI Provider
            </label>
            <div className="grid grid-cols-2 gap-4">
              {aiProviders.map((provider) => (
                <button
                  key={provider.id}
                  type="button"
                  onClick={() => setAiProvider(provider.id)}
                  className={`p-4 rounded-lg border-2 transition-all text-left ${
                    aiProvider === provider.id
                      ? 'border-accent-blue bg-accent-blue/10'
                      : 'border-border hover:border-accent-blue/50'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="font-semibold text-text-primary">{provider.name}</div>
                    </div>
                    {aiProvider === provider.id && (
                      <div className="w-5 h-5 rounded-full bg-accent-blue flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-text-secondary">{provider.description}</p>
                </button>
              ))}
            </div>
            <p className="mt-2 text-xs text-text-secondary">
              Your custom weights will be applied to the selected AI provider's predictions
            </p>
          </div>

          {/* Attributes Section */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-text-primary">
                Attribute Weights
                <span className="ml-2 text-xs text-text-secondary">
                  (Drag to reorder by priority, adjust weights)
                </span>
              </label>
              <button
                onClick={handleAutoBalance}
                disabled={loading}
                className="text-xs px-3 py-1 bg-bg-secondary hover:bg-bg-secondary/80 text-accent-blue rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Auto-Balance
              </button>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent-blue"></div>
              </div>
            ) : (
              <>
                {/* Weight Total Indicator */}
                <div className={`mb-4 p-3 rounded-lg ${weightError ? 'bg-danger/10 border border-danger' : 'bg-success/10 border border-success'}`}>
                  <div className="flex items-center justify-between text-sm">
                    <span className={weightError ? 'text-danger' : 'text-success'}>
                      Total Weight: {totalWeight}%
                    </span>
                    {weightError && (
                      <span className="text-danger text-xs">Must equal 100%</span>
                    )}
                  </div>
                  <div className="mt-2 w-full bg-bg-secondary rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${weightError ? 'bg-danger' : 'bg-success'}`}
                      style={{ width: `${Math.min(totalWeight, 100)}%` }}
                    />
                  </div>
                </div>

                {/* Attributes List */}
                <div className="space-y-2">
                  {attributes.map((attribute, index) => (
                <div
                  key={attribute.id}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={handleDragEnd}
                  className={`p-4 rounded-lg border transition-all cursor-move ${
                    draggedIndex === index
                      ? 'border-accent-blue bg-accent-blue/10 scale-105'
                      : 'border-border bg-bg-secondary hover:border-accent-blue/50'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    {/* Drag Handle */}
                    <div className="mt-1">
                      <Bars3Icon className="w-5 h-5 text-text-secondary" />
                    </div>

                    {/* Priority Number */}
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-accent-blue/20 flex items-center justify-center">
                      <span className="text-sm font-bold text-accent-blue">
                        {index + 1}
                      </span>
                    </div>

                    {/* Attribute Info */}
                    <div className="flex-grow min-w-0">
                      <div className="font-medium text-text-primary mb-1">
                        {attribute.display_name}
                      </div>
                      <div className="text-xs text-text-secondary">
                        {attribute.description}
                      </div>
                    </div>

                    {/* Weight Input */}
                    <div className="flex-shrink-0 w-32">
                      <div className="flex items-center gap-2">
                        <input
                          type="range"
                          min="0"
                          max="100"
                          value={attribute.weight}
                          onChange={(e) => handleWeightChange(index, e.target.value)}
                          className="flex-grow"
                        />
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={attribute.weight}
                          onChange={(e) => handleWeightChange(index, e.target.value)}
                          className="w-16 px-2 py-1 bg-bg-card border border-border rounded text-sm text-center"
                        />
                        <span className="text-sm text-text-secondary">%</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
                </div>
              </>
            )}
          </div>

          {/* Help Text */}
          {!loading && (
            <div className="p-4 bg-bg-secondary rounded-lg border border-border">
            <h4 className="text-sm font-semibold text-text-primary mb-2">
              How to use this model:
            </h4>
            <ul className="text-xs text-text-secondary space-y-1">
              <li>• <strong>Drag</strong> attributes to reorder them by priority</li>
              <li>• <strong>Adjust sliders</strong> to fine-tune the weight of each attribute</li>
              <li>• Weights must add up to <strong>100%</strong></li>
              <li>• Higher priority attributes will have more influence on predictions</li>
              <li>• Use <strong>Auto-Balance</strong> to distribute weights equally</li>
            </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-bg-card/95 backdrop-blur-sm border-t border-border p-6 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-border rounded-lg hover:bg-bg-secondary transition-colors text-text-primary"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={weightError || !modelName.trim() || saving || loading}
            className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
              weightError || !modelName.trim() || saving || loading
                ? 'bg-gray-500 text-gray-300 cursor-not-allowed'
                : 'bg-accent-blue hover:bg-accent-blue/90 text-bg-primary'
            }`}
          >
            {saving ? 'Saving...' : (model ? 'Save Changes' : 'Create Model')}
          </button>
        </div>
      </div>
    </div>
  );
}
